import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage, type MessagePayload, type Messaging } from 'firebase/messaging';
import { firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

// Fuera de state: el meta-reducer de logout borra state y cada login daría de alta un dispositivo nuevo.
const DEVICE_ID_KEY = 'pushDeviceId';
// Preferencia del afiliado (toggle del panel). Solo 'false' desactiva: sin valor se considera activo.
const ENABLED_KEY = 'pushEnabled';

@Injectable({ providedIn: 'root' })
export class PushService {
  // HttpClient sin interceptores: sin modal de error y sin logout ante un 401.
  private http = new HttpClient(inject(HttpBackend));
  private zone = inject(NgZone);
  private app: FirebaseApp | null = null;
  private listening = false;
  // Registro en curso: login y dashboard lo disparan casi juntos y no hace falta repetirlo.
  private registering: Promise<boolean> | null = null;

  /** Push recibidos con la pestaña abierta. */
  public readonly foregroundMessages$ = new Subject<MessagePayload>();

  public async isAvailable(): Promise<boolean> {
    return 'Notification' in window && 'serviceWorker' in navigator && (await isSupported());
  }

  public get permission(): NotificationPermission | 'unsupported' {
    return 'Notification' in window ? Notification.permission : 'unsupported';
  }

  /** Preferencia del afiliado: false si desactivó las notificaciones desde el toggle. */
  public get enabled(): boolean {
    return localStorage.getItem(ENABLED_KEY) !== 'false';
  }

  /** Activa desde el toggle: guarda la preferencia y registra pidiendo permiso si hace falta. */
  public async enable(sessionToken: string): Promise<boolean> {
    localStorage.removeItem(ENABLED_KEY);
    const registered = await this.register(sessionToken, true);
    return registered;
  }

  /** Desactiva desde el toggle: guarda la preferencia y da de baja el navegador. */
  public async disable(sessionToken: string): Promise<void> {
    localStorage.setItem(ENABLED_KEY, 'false');
    await this.unregister(sessionToken);
  }

  /**
   * Registra el navegador. Con askPermission=false solo registra si el permiso ya
   * estaba concedido (no muestra el diálogo del navegador).
   */
  public register(sessionToken: string, askPermission = false): Promise<boolean> {
    this.registering ??= this.doRegister(sessionToken, askPermission).finally(() => (this.registering = null));
    return this.registering;
  }

  private async doRegister(sessionToken: string, askPermission: boolean): Promise<boolean> {
    if (!sessionToken || !(await this.canRegister(askPermission))) return false;
    try {
      const messaging = getMessaging(this.getApp());
      const fcmToken = await this.getFcmToken(messaging);
      await this.saveDevice(sessionToken, fcmToken);
      this.listenForegroundMessages(messaging);
      return true;
    } catch (error) {
      console.warn('push: no se pudo registrar el navegador', error);
      return false;
    }
  }

  /** Soporte del navegador, preferencia del afiliado y permiso (pidiéndolo solo si askPermission). */
  private async canRegister(askPermission: boolean): Promise<boolean> {
    if (!(await this.isAvailable())) return false;
    // Si el afiliado las desactivó desde el toggle, solo el propio toggle las vuelve a activar.
    if (!this.enabled) return false;
    if (askPermission && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    return Notification.permission === 'granted';
  }

  /** Token de Firebase para este navegador, con el service worker ya activo. */
  private async getFcmToken(messaging: Messaging): Promise<string> {
    await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    // Recién instalado, getToken falla al leer pushManager: se espera a que esté activo.
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    return getToken(messaging, {
      serviceWorkerRegistration,
      // Sin VAPID key propia, el SDK usa la clave por defecto de Firebase (alcanza para los envíos por FCM).
      ...(environment.firebaseVapidKey ? { vapidKey: environment.firebaseVapidKey } : {}),
    });
  }

  private saveDevice(sessionToken: string, fcmToken: string): Promise<unknown> {
    return firstValueFrom(
      this.http.post(
        `${environment.apiUrl}devices`,
        { deviceId: this.deviceId, platform: 'web', fcmToken, appVersion: 'web' },
        { headers: this.authHeaders(sessionToken) }
      )
    );
  }

  /** Se suscribe una sola vez a los push que llegan con la pestaña abierta. */
  private listenForegroundMessages(messaging: Messaging): void {
    if (this.listening) return;
    this.listening = true;
    onMessage(messaging, payload => {
      // Dentro de la zona de Angular para que la vista se actualice al recibir el push.
      this.zone.run(() => this.foregroundMessages$.next(payload));
    });
  }

  /**
   * Con la pestaña visible el navegador no muestra el push solo: se muestra desde acá, a través
   * del service worker, igual que en segundo plano. El clic lo maneja firebase-messaging-sw.js.
   */
  public async showNotification(payload: MessagePayload): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      const orderNumber = payload.data?.['orderNumber'];
      await registration?.showNotification(payload.notification?.title ?? 'Nobis', {
        body: payload.notification?.body,
        icon: '/assets/img/logos/nobis-icon.png',
        // Los push de la misma orden se reemplazan entre sí, como hace el backend en segundo plano
        tag: orderNumber ? `order-${orderNumber}` : undefined,
        data: payload.data,
      });
    } catch (error) {
      console.warn('push: no se pudo mostrar la notificación', error);
    }
  }

  /** Baja del navegador. Llamar en el logout con el token capturado ANTES de despachar logout. */
  public async unregister(sessionToken: string | null): Promise<void> {
    if (!sessionToken || !localStorage.getItem(DEVICE_ID_KEY)) return;
    try {
      await firstValueFrom(
        this.http.delete(`${environment.apiUrl}devices/${encodeURIComponent(this.deviceId)}`, {
          headers: this.authHeaders(sessionToken),
        })
      );
    } catch (error) {
      console.warn('push: no se pudo dar de baja el navegador', error);
    }
  }

  private get deviceId(): string {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  }

  private authHeaders(token: string) {
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  private getApp(): FirebaseApp {
    return (this.app ??= initializeApp(environment.firebase));
  }
}
